import { useEffect, useMemo, useState } from 'react';
import { money } from '../lib';
import { records, type Body, type NamedId, type RecordOptions, type VendorRecord as Rec } from '../records-api';
import {
  ChangesCard, FieldGrid, OutcomeLine, SaveBar, SystemOfRecordNote, VendorPicker, WeekdayPicker, dayNames, errText, useRecordForm,
  type FieldDef, type Outcome,
} from '../records';
import { PairEditor } from './Camps';

// The full vendor record under the Vendors detail (parity review 2026-10-16, Tier 1 #4): delivery and
// minimum order, price-feed column map and auto-approve, EDI and Dropbox, contacts, relationships and
// assigned bids, and each camp's delivery settings. The PO email lists and the per-camp account
// numbers stay where they were (Vendors.tsx), because that screen shows where the next PO goes.

const MIN_PER_LABEL: Record<string, string> = { order: 'per order', day: 'per delivery day' };

const deliveryFields = (o: RecordOptions): FieldDef[] => [
  { key: 'minOrderAmount', col: 'min_order_amount', label: 'Minimum order ($)', kind: 'number', placeholder: 'none' },
  { key: 'minOrderPer', col: 'min_order_per', label: 'Minimum applies', kind: 'select', options: o.minOrderPer.map((v) => ({ value: v, label: MIN_PER_LABEL[v] ?? v })) },
  { key: 'minOrderQty', col: 'min_order_qty', label: 'Minimum quantity', kind: 'number', placeholder: 'none' },
  { key: 'cutoffTime', col: 'cutoff_time', label: 'Cut-off time', kind: 'text', placeholder: 'e.g. 14:30' },
  { key: 'orderLeadTime', col: 'order_lead_time', label: 'Order lead time (days)', kind: 'text' },
  { key: 'deliveryDateRequired', col: 'delivery_date_required', label: 'Delivery date required', kind: 'bool' },
];
const FEED_FIELDS: FieldDef[] = [
  { key: 'itemCol', col: 'item_col', label: 'Item number — column', kind: 'number' },
  { key: 'caseCol', col: 'case_col', label: 'Case price — column', kind: 'number' },
  { key: 'splitCol', col: 'split_col', label: 'Split price — column', kind: 'number' },
  { key: 'splitsIncluded', col: 'splits_included', label: 'Splits are separate lines', kind: 'bool' },
  { key: 'autoApproveFeed', col: 'auto_approve_feed', label: 'Auto-approve price changes', kind: 'bool' },
];
const EDI_FIELDS: FieldDef[] = [
  { key: 'ediOpcode', col: 'edi_opcode', label: 'EDI OpCode / prefix', kind: 'text' },
  { key: 'dropboxPath', col: 'dropbox_path', label: 'Dropbox path', kind: 'text' },
  { key: 'dropboxFileName', col: 'dropbox_file_name', label: 'Dropbox file name', kind: 'text', placeholder: 'Vendor_#INVOICE#_#INVOICEDATE#.pdf' },
  { key: 'reportVendorEmails', col: 'report_vendor_emails', label: 'Invoice corrections email', kind: 'email' },
  { key: 'visibleOnVendorList', col: 'visible_on_vendor_list', label: 'Visible on vendor list', kind: 'bool' },
  { key: 'showCheckoutAlert', col: 'show_checkout_alert', label: 'Checkout alert', kind: 'bool' },
  { key: 'invoiceReview', col: 'invoice_review', label: 'Invoice review', kind: 'bool' },
  { key: 'autoLockPackages', col: 'auto_lock_packages', label: 'Auto-lock packages', kind: 'bool' },
  { key: 'displayOnPo', col: 'display_on_po', label: 'Printed on the PO to the vendor', kind: 'textarea' },
  { key: 'internalInvNotes', col: 'internal_inv_notes', label: 'Internal invoice notes', kind: 'textarea' },
];
const CONTACT_FIELDS: FieldDef[] = [
  { key: 'primaryContact', col: 'primary_contact', label: 'Primary contact', kind: 'text' },
  { key: 'officePhone', col: 'office_phone', label: 'Salesperson office phone', kind: 'text' },
  { key: 'cellPhone', col: 'cell_phone', label: 'Salesperson cell phone', kind: 'text' },
  { key: 'custsvcPhone', col: 'custsvc_phone', label: 'Customer service phone', kind: 'text' },
  { key: 'vendorNote', col: 'vendor_note', label: 'H&H vendor note (shown to buyers)', kind: 'textarea' },
  { key: 'notes', col: 'notes', label: 'Notes', kind: 'textarea' },
];

export function VendorRecordSections({ id, canEdit }: { id: number; canEdit: boolean }) {
  const [d, setD] = useState<Rec | null>(null);
  const [opts, setOpts] = useState<RecordOptions | null>(null);
  const [err, setErr] = useState('');
  const [out, setOut] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    records.vendor(id).then(setD).catch((e) => setErr(errText(e)));
    records.options().then(setOpts).catch((e) => setErr(errText(e)));
  }, [id]);

  /** Run one save; its outcome shows beside the control that made it (`tag`). Answers whether it was accepted. */
  async function run(tag: string, fn: () => Promise<{ changed: string[] } & Partial<Rec>>, what: string): Promise<boolean> {
    setBusy(tag); setOut(null);
    try {
      const r = await fn();
      setD(r.vendor ? (r as Rec) : await records.vendor(id));
      setOut({ tag, bad: false, text: r.changed.length ? `Saved ${what}.` : 'Nothing changed.' });
      return true;
    } catch (e) {
      setOut({ tag, bad: true, text: errText(e) });
      return false;
    } finally { setBusy(''); }
  }
  const save = (tag: string, body: Body, what: string) => run(tag, () => records.saveVendor(id, body), what);
  const savePair = (orgId: number, body: Body) => run(`pair-${orgId}`, () => records.saveVendorCamp(id, orgId, body), 'the camp\'s delivery settings');
  const outFor = (tag: string) => (out && (out.tag === tag || out.tag.startsWith(`${tag}-`)) ? out : null);

  // One object per loaded record: the forms reset to it whenever it changes, so it must not change on
  // every render (a save's outcome re-renders this, and would wipe what someone is typing).
  const rec = useMemo(() => (d ? { ...d.vendor, ...(d.profile ?? {}), ...(d.feedMap ?? {}) } : {}) as Record<string, unknown>, [d]);
  if (!d || !opts) return err ? <p className="msg bad">⚠ {err}</p> : <p className="loading">Loading the vendor record…</p>;

  return (
    <>
      <h2 className="mr-section">Vendor record</h2>
      <SystemOfRecordNote />
      <DeliveryCard rec={rec} d={d} opts={opts} canEdit={canEdit} busy={busy === 'delivery'} outcome={outFor('delivery')} onSave={(b) => save('delivery', b, 'delivery and minimum order')} />
      <FormCard title="Price feed" lead="Which spreadsheet column holds each value (1 = column A). A feed is read by these numbers, not by its header row."
        defs={FEED_FIELDS} rec={rec} canEdit={canEdit} busy={busy === 'feed'} outcome={outFor('feed')} onSave={(b) => save('feed', b, 'the price-feed map')} />
      <FormCard title="EDI, Dropbox and invoices" defs={EDI_FIELDS} rec={rec} canEdit={canEdit} busy={busy === 'edi'} outcome={outFor('edi')} onSave={(b) => save('edi', b, 'EDI and invoice settings')} />
      <FormCard title="Contacts and notes" defs={CONTACT_FIELDS} rec={rec} canEdit={canEdit} busy={busy === 'contacts'} outcome={outFor('contacts')} onSave={(b) => save('contacts', b, 'contacts and notes')} />
      <RelationshipsCard d={d} canEdit={canEdit} busy={busy === 'rel'} outcome={outFor('rel')} onSave={(b) => save('rel', b, 'the relationships')} />
      <CampDeliveryCard d={d} canEdit={canEdit} busy={busy} outcome={outFor('pair')} onSave={savePair} />
      <ChangesCard changes={d.changes} />
    </>
  );
}

function FormCard({ title, lead, defs, rec, canEdit, busy, outcome, onSave }: {
  title: string; lead?: string; defs: FieldDef[]; rec: Record<string, unknown>; canEdit: boolean; busy: boolean; outcome: Outcome | null;
  onSave: (b: Body) => void;
}) {
  const f = useRecordForm(defs, rec);
  return (
    <section className="card">
      <h2>{title}</h2>
      {lead && <p className="muted mr-lead">{lead}</p>}
      <FieldGrid defs={defs} form={f.form} onChange={f.setForm} disabled={!canEdit} />
      {canEdit && <SaveBar dirty={f.dirty} busy={busy} onSave={() => onSave(f.changes)} onReset={f.reset} outcome={outcome} />}
    </section>
  );
}

function DeliveryCard({ rec, d, opts, canEdit, busy, outcome, onSave }: {
  rec: Record<string, unknown>; d: Rec; opts: RecordOptions; canEdit: boolean; busy: boolean; outcome: Outcome | null; onSave: (b: Body) => void;
}) {
  const defs = useMemo(() => deliveryFields(opts), [opts]);
  const f = useRecordForm(defs, rec);
  const initDays = d.profile?.allowed_week_days ?? [];
  const initExcluded = d.profile?.excluded_days ?? [];
  const [days, setDays] = useState<number[]>(initDays);
  const [excluded, setExcluded] = useState<string[]>(initExcluded);
  const [newDay, setNewDay] = useState('');
  useEffect(() => { setDays(d.profile?.allowed_week_days ?? []); setExcluded(d.profile?.excluded_days ?? []); }, [d]);
  const body: Body = { ...f.changes };
  if (JSON.stringify(days) !== JSON.stringify(initDays)) body.allowedWeekDays = days;
  if (JSON.stringify(excluded) !== JSON.stringify(initExcluded)) body.excludedDays = excluded;
  const upcoming = excluded.filter((x) => x >= new Date().toISOString().slice(0, 10));
  return (
    <section className="card">
      <h2>Delivery and minimum order</h2>
      <p className="muted mr-lead">The vendor's defaults (Adobe's Delivery Settings and Minimum Order Amount tabs). A camp's own settings below take precedence for that camp.</p>
      <FieldGrid defs={defs} form={f.form} onChange={f.setForm} disabled={!canEdit} />
      <div className="mr-row">
        <div><div className="iw-lbl">Delivery days</div><WeekdayPicker days={days} onChange={setDays} disabled={!canEdit} /></div>
        <div>
          <div className="iw-lbl">Excluded days ({upcoming.length} upcoming{excluded.length > upcoming.length ? `, ${excluded.length - upcoming.length} past` : ''})</div>
          <div className="mr-chips">
            {excluded.map((x) => (
              <span key={x} className={`pill mr-chip ${upcoming.includes(x) ? '' : 'mr-past'}`}>
                {x}{canEdit && <button className="mr-x" aria-label={`Remove ${x}`} onClick={() => setExcluded(excluded.filter((y) => y !== x))}>×</button>}
              </span>
            ))}
            {canEdit && (
              <span className="mr-add">
                <input type="date" className="search short mr-date" value={newDay} onChange={(e) => setNewDay(e.target.value)} />
                <button className="btn-sm ghost" disabled={!newDay || excluded.includes(newDay)}
                  onClick={() => { setExcluded([...excluded, newDay].sort()); setNewDay(''); }}>Add</button>
              </span>
            )}
          </div>
        </div>
      </div>
      {canEdit && <SaveBar dirty={Object.keys(body).length > 0} busy={busy} onSave={() => onSave(body)} outcome={outcome}
        onReset={() => { f.reset(); setDays(initDays); setExcluded(initExcluded); }} />}
    </section>
  );
}

function RelationshipsCard({ d, canEdit, busy, outcome, onSave }: {
  d: Rec; canEdit: boolean; busy: boolean; outcome: Outcome | null; onSave: (b: Body) => void;
}) {
  const id = d.vendor.id;
  const p = d.profile;
  const nameOf = (x: number | null | undefined) => (x == null ? '—' : x === id ? `${d.vendor.name ?? 'this vendor'} (itself)` : d.related.find((r) => r.id === x)?.name ?? `#${x}`);
  const [sibling, setSibling] = useState<number | null>(p?.sibling_of ?? null);
  const [siblingName, setSiblingName] = useState('');
  const [isParent, setIsParent] = useState(p?.is_parent === true);
  const [related, setRelated] = useState<NamedId[]>(d.related.filter((r) => (p?.related_vendor_ids ?? []).includes(r.id)));
  useEffect(() => {
    setSibling(d.profile?.sibling_of ?? null); setIsParent(d.profile?.is_parent === true);
    setRelated(d.related.filter((r) => (d.profile?.related_vendor_ids ?? []).includes(r.id)));
  }, [d]);
  const body: Body = {};
  if (sibling !== (p?.sibling_of ?? null)) body.siblingOf = sibling;
  if (isParent !== (p?.is_parent === true)) body.isParent = isParent;
  const relIds = related.map((r) => r.id);
  if (JSON.stringify(relIds) !== JSON.stringify(p?.related_vendor_ids ?? [])) body.relatedVendorIds = relIds;
  return (
    <section className="card">
      <h2>Relationships</h2>
      <p className="muted mr-lead">A family of vendors shares one parent: each sibling names the parent, and the parent names itself.</p>
      <div className="mr-row">
        <div className="stack mr-tight">
          <div className="iw-lbl">Sibling of (parent vendor)</div>
          <div>{siblingName || nameOf(sibling)} {canEdit && sibling != null && <button className="link" onClick={() => { setSibling(null); setSiblingName(''); }}>clear</button>}</div>
          {canEdit && (
            <div className="actions">
              <VendorPicker placeholder="Pick the parent vendor…" onPick={(vid, n) => { setSibling(vid); setSiblingName(n); }} />
              <button className="btn-sm ghost" onClick={() => { setSibling(id); setSiblingName(`${d.vendor.name ?? 'this vendor'} (itself)`); }}>Itself</button>
            </div>
          )}
          <label className="chk"><input type="checkbox" checked={isParent} disabled={!canEdit} onChange={(e) => setIsParent(e.target.checked)} /> Is a parent vendor</label>
        </div>
        <div className="stack mr-tight">
          <div className="iw-lbl">Related vendors</div>
          <div className="mr-chips">
            {related.map((r) => (
              <span key={r.id} className="pill mr-chip">{r.name ?? `#${r.id}`}
                {canEdit && <button className="mr-x" aria-label="Remove" onClick={() => setRelated(related.filter((x) => x.id !== r.id))}>×</button>}
              </span>
            ))}
            {related.length === 0 && <span className="muted">None.</span>}
          </div>
          {canEdit && <VendorPicker placeholder="Add a related vendor…" exclude={[id, ...relIds]} onPick={(vid, n) => setRelated([...related, { id: vid, name: n }])} />}
        </div>
      </div>
      {canEdit && <SaveBar dirty={Object.keys(body).length > 0} busy={busy} onSave={() => onSave(body)} outcome={outcome} />}
      <div className="mr-row">
        <div>
          <div className="iw-lbl">Vendors that name it as parent ({d.siblings.length})</div>
          <p className="small">{d.siblings.length ? d.siblings.map((s) => s.name ?? `#${s.id}`).join(', ') : '—'}</p>
        </div>
        <div>
          <div className="iw-lbl">Assigned bids ({d.bids.length})</div>
          <p className="small">{d.bids.length ? d.bids.map((b) => b.name).join(', ') : '—'}</p>
        </div>
      </div>
    </section>
  );
}

function CampDeliveryCard({ d, canEdit, busy, outcome, onSave }: {
  d: Rec; canEdit: boolean; busy: string; outcome: Outcome | null; onSave: (orgId: number, b: Body) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const [filter, setFilter] = useState('');
  const rows = d.camps.filter((c) => !filter || c.camp.toLowerCase().includes(filter.toLowerCase()));
  return (
    <section className="card">
      <h2>Assigned camps — delivery settings ({d.camps.filter((c) => c.is_active).length} active)</h2>
      <p className="muted mr-lead">Each camp's own minimum, delivery days, cut-off and lead time with this vendor (Adobe's Assigned Customers tab). Checkout applies these to the camp's next order.</p>
      <div className="toolbar"><input className="search" placeholder="Filter camps…" value={filter} onChange={(e) => setFilter(e.target.value)} />{editing == null && <OutcomeLine o={outcome} />}</div>
      <div className="mr-scroll mr-tall">
        <table>
          <thead><tr><th>Camp</th><th>Assignment</th><th className="num">Min order</th><th>Delivery days</th><th>Cut-off</th><th>Lead time</th><th></th></tr></thead>
          <tbody>
            {rows.map((c) => (
              editing === c.org_id ? (
                <tr key={c.org_id}><td colSpan={7}>
                  <PairEditor name={c.camp} pair={c} busy={busy === `pair-${c.org_id}`} outcome={outcome} onCancel={() => setEditing(null)}
                    onSave={async (b) => { if (await onSave(c.org_id, b)) setEditing(null); }} />
                </td></tr>
              ) : (
                <tr key={c.org_id} className={c.is_active ? undefined : 'inactive'}>
                  <td>{c.camp} <span className="mono">#{c.org_id}</span></td>
                  <td>{c.is_active ? <span className="pill type">active</span> : <span className="muted">inactive</span>}</td>
                  <td className="num">{c.min_order_amount ? money(c.min_order_amount) : '—'}</td>
                  <td className="small">{dayNames(c.allowed_week_days)}</td>
                  <td>{c.cutoff_time ?? '—'}</td>
                  <td>{c.order_lead_time ?? '—'}</td>
                  <td>{canEdit && <button className="btn-sm ghost" disabled={busy !== '' || editing != null} onClick={() => setEditing(c.org_id)}>Edit</button>}</td>
                </tr>
              )
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="loading">No camps.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
